import { closePackageModal, getPackageModalHost, openPackageModal } from 'lib/openPackageModal'
import React, { useCallback, useEffect, useRef, useState } from 'react'
import PackageModal from './PackageModal'

// Copy mirrors the legacy swal (document_requirement.js, packages.js#generateOrUpdateCompiledPdfSwal).
const COPY = {
  generate: {
    title: 'generate application package?',
    cta: 'generate',
  },
  update: {
    title: 'update application package?',
    cta: 'update',
  },
}

// PackageCompiler has no failure hook (beginCompiling's $.ajax has no .fail(); pollCompilationStatus's
// .then() has no .catch()) -- a 403/500 leaves callbacks silent forever. This stall timer is a local
// guard only; it does not touch the shared package_compiler.js global.
const STALL_TIMEOUT_MS = 15000

const GeneratePackageModal = ({ packageHashedId, mode, redactHelpUrl }) => {
  const [status, setStatus] = useState('idle') // idle | compiling | error
  const [progress, setProgress] = useState(0)
  const compilerRef = useRef(null)
  const stallTimerRef = useRef(null)
  const attemptIdRef = useRef(0)

  const copy = COPY[mode] || COPY.generate

  const clearStallTimer = useCallback(() => {
    if (stallTimerRef.current) {
      clearTimeout(stallTimerRef.current)
      stallTimerRef.current = null
    }
  }, [])

  const armStallTimer = useCallback(() => {
    clearStallTimer()
    stallTimerRef.current = setTimeout(() => {
      attemptIdRef.current += 1 // invalidate this attempt so its stale callbacks become no-ops
      compilerRef.current = null // retry-unlock
      setStatus('error')
    }, STALL_TIMEOUT_MS)
  }, [clearStallTimer])

  useEffect(() => clearStallTimer, [clearStallTimer])

  const handleConfirm = useCallback(() => {
    if (status === 'compiling' || compilerRef.current) return // double-click guard

    setStatus('compiling')
    setProgress(0)
    armStallTimer()

    const attemptId = (attemptIdRef.current += 1)
    // Stall timeout / a later retry can invalidate attemptId while this instance is still
    // polling in the background -- guard every callback so a stale instance can't clobber
    // the active attempt's state.
    const isStaleAttempt = () => attemptIdRef.current !== attemptId

    compilerRef.current = new window.PackageCompiler({
      packageHashedId,
      statusUpdatedCallback: (data) => {
        if (isStaleAttempt()) return
        armStallTimer()
        const total = data?.progress?.total || 0
        const current = data?.progress?.current || 0
        setProgress(total > 0 ? (current / total) * 100 : 0)
      },
      compiledCallback: () => {
        if (isStaleAttempt()) return
        clearStallTimer()
        compilerRef.current = null
        // Soft link to DOME-1384/1385: if next_steps isn't registered yet, this 403s. openPackageModal
        // only tears down this modal once the replacement is ready, so a 403 leaves this modal in
        // place instead of vanishing to a blank host.
        openPackageModal(packageHashedId, 'next_steps')
          .catch(() => {})
          .finally(() => {
            setStatus('idle')
          })
      },
    })
  }, [status, packageHashedId, armStallTimer, clearStallTimer])

  const handleCancel = useCallback(() => {
    if (status === 'compiling') return // no-op while compiling, matches legacy showCancelButton: false
    closePackageModal()
  }, [status])

  const isGenerating = status === 'compiling'

  return (
    <PackageModal isOpen={true} onClose={handleCancel} closeOnOutsideClick={false}>
      <div style={{ width: 380 }}>
        <div className="dome-d-flex dome-flex-column dome-gap18">
          <div className="dome-d-flex dome-flex-column dome-gap12">
            <span>
              <i
                style={{ fontSize: '42px' }}
                className="fa-light fa-circle-exclamation dome-color-dark-green"
              />
            </span>
            <span className="dome-h2 dome-color-dark-green">{copy.title}</span>
          </div>

          {isGenerating && (
            <div className="dome-d-flex dome-flex-column" style={{ gap: 2 }}>
              <span style={{ fontSize: 11 }} className="dome-color-dark-grey">
                generating ...
              </span>
              <div style={{ height: 6, width: `100%`, background: '#DBDBDB', borderRadius: 12 }}>
                <div
                  style={{
                    height: 6,
                    width: `${progress}%`,
                    background: '#4C7E77',
                    borderRadius: 12,
                    transition: 'width 0.4s ease',
                  }}
                />
              </div>
            </div>
          )}

          <div
            style={{ opacity: isGenerating ? 0.3 : 1, pointerEvents: isGenerating ? 'none' : 'auto' }}
            className="dome-d-flex dome-flex-column dome-gap12"
          >
            <span className="dome-p1 dome-color-dark-grey">
              Generate your application package to assemble all completed forms into a single file.
            </span>
            <span className="dome-p1 dome-color-dark-grey">
              Your latest updates will be included and ready for review or submission.
            </span>
            <div className="dome-d-flex dome-align-center dome-gap6">
              <i className="fa-solid fa-highlighter dome-color-dark-grey" />
              <span className="dome-p2 dome-text-w500 dome-color-dark-grey">did you redact?</span>
              <a
                href={redactHelpUrl || '#'}
                target={redactHelpUrl ? '_blank' : undefined}
                rel="noreferrer"
                className="dome-d-flex dome-align-center dome-gap3"
              >
                <span className="dome-p2 dome-text-w500 dome-color-aqua">learn how</span>
                <i className="fa-light fa-arrow-right-long fa-xs dome-color-aqua" />
              </a>
            </div>
          </div>

          <div
            style={{ opacity: isGenerating ? 0.3 : 1, pointerEvents: isGenerating ? 'none' : 'auto' }}
            className="dome-d-flex dome-justify-between"
          >
            <button
              type="button"
              onClick={handleCancel}
              disabled={isGenerating}
              className="dome-btn dome-btn-link dome-btn-link-med-grey dome-btn-link-cancel"
            >
              cancel
            </button>
            <button
              type="button"
              onClick={handleConfirm}
              disabled={isGenerating}
              className="dome-btn dome-btn-base dome-btn-dark-green"
            >
              {copy.cta}
            </button>
          </div>
        </div>
      </div>
    </PackageModal>
  )
}

export default GeneratePackageModal
